import React, { useEffect, useState } from 'react';
import { ClassNameProp } from '../common-types';
import { Maskable } from './LoadMask';
import { defaultIndividualProviderAvatarPath, isEmptyString } from '../../util';
import { NameCircle } from './NameCircle';
import { showError } from '../../lib';

/**
 * Properties for a {@link PersonImage} component.
 *
 * @author agd
 */
export interface PersonImageProps extends ClassNameProp {
  /**
   * image to display.
   */
  image?: string;
  /**
   * Method for loading an image asynchronously.
   */
  loadImage?: () => Promise<string | null | undefined>;
  /**
   * Determines how the component will behave if no image for displaying is present.
   * - 'nameCircle' {@link NameCircle} component with {@link firstName} and {@link lastName}
   * - 'defaultAvatar' img tag with the default avatar image
   */
  emptyState: 'nameCircle' | 'defaultAvatar';
  /**
   * Person firstName for empty 'nameCircle' state.
   */
  firstName?: string;
  /**
   * Person lastName for empty 'nameCircle' state.
   */
  lastName?: string;
}

/**
 * Displays a person's image in a nice dynamic way and handles showing an 'empty' state
 * when no image is present.
 *
 * @param properties a {@link PersonImageProps} object
 * @return the created {@link JSX.Element}.
 * @author agd
 * @constructor
 */
// overload to make sure firstName and lastName is required in 'nameCircle' empty mode
export function PersonImage(
  properties: PersonImageProps & {
    emptyState: 'nameCircle';
    firstName: string;
    lastName: string;
  }
): JSX.Element;

// overload to make sure firstName and lastName is required when the emptyState is dynamic
export function PersonImage(
  properties: PersonImageProps & {
    emptyState: 'nameCircle' | 'defaultAvatar';
    firstName: string;
    lastName: string;
  }
): JSX.Element;

// overload to make sure firstName and lastName is optional in 'defaultAvatar' empty mode
export function PersonImage(
  properties: PersonImageProps & {
    emptyState: 'defaultAvatar';
    firstName?: string;
    lastName?: string;
  }
): JSX.Element;

// implementation
export function PersonImage({
  image = '',
  loadImage,
  emptyState = 'nameCircle',
  firstName,
  lastName,
  className = ''
}: PersonImageProps): JSX.Element {
  // internal state for using `loadImage`
  const [loadedImage, setShownImage] = useState('');
  const [loading, setLoading] = useState(false);

  // load person's profile picture
  useEffect(() => {
    if (loadImage) {
      setLoading(true);
      loadImage()
        .then(value => {
          if (value) {
            setShownImage(value);
          }
        })
        .catch(showError)
        .finally(() => setLoading(false));
    }
  }, [loadImage]);

  const shownImage = loadedImage || image;
  const clazz = 'person-image-image';
  const imgAlt = 'person-image.png';

  return (
    <Maskable
      masked={loading}
      className={`ehs-person-image ${className}`.trim()}
    >
      {isEmptyString(shownImage) ? (
        emptyState === 'nameCircle' ? (
          <NameCircle
            className={clazz}
            firstName={firstName}
            lastName={lastName}
          />
        ) : (
          <img
            src={defaultIndividualProviderAvatarPath}
            alt={imgAlt}
            aria-hidden
            className={clazz}
          />
        )
      ) : (
        <img
          src={`data:image/png;base64,${shownImage}`}
          alt={imgAlt}
          aria-hidden
          className={clazz}
        />
      )}
    </Maskable>
  );
}
